import { Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from './components/layout';
import { AuthProvider, ProtectedRoute } from './components/auth';
import { LoginPage, RegisterPage } from './pages';
import { BoardContainer, DEMO_BOARD } from './components/board';

/**
 * Home Page
 */
function HomePage() {
  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-100 mb-6">Good morning!</h1>

        {/* Quick actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <QuickActionCard
            title="Create Board"
            description="Start a new board from scratch"
            icon="+"
          />
          <QuickActionCard
            title="From Template"
            description="Use a pre-built template"
            icon="T"
          />
          <QuickActionCard
            title="Import Data"
            description="Import from Excel or CSV"
            icon="I"
          />
          <QuickActionCard
            title="Invite Team"
            description="Add team members"
            icon="U"
          />
        </div>

        {/* Recent boards */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-100 mb-4">Recent Boards</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <BoardCard
              name="Sprint Planning"
              workspace="Engineering"
              lastUpdated="2 hours ago"
              color="#0073ea"
            />
            <BoardCard
              name="Bug Tracker"
              workspace="Engineering"
              lastUpdated="5 hours ago"
              color="#00c875"
            />
            <BoardCard
              name="Design System"
              workspace="Design"
              lastUpdated="1 day ago"
              color="#a25ddc"
            />
          </div>
        </section>

        {/* Favorites */}
        <section>
          <h2 className="text-lg font-semibold text-gray-100 mb-4">Favorites</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <BoardCard
              name="Roadmap"
              workspace="Engineering"
              lastUpdated="3 days ago"
              color="#ff5ac4"
              isFavorite
            />
          </div>
        </section>
      </div>
    </div>
  );
}

interface QuickActionCardProps {
  title: string;
  description: string;
  icon: string;
}

function QuickActionCard({ title, description, icon }: QuickActionCardProps) {
  return (
    <button className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-lg p-4 text-left hover:border-primary-500/50 transition-colors group">
      <div className="h-10 w-10 rounded-lg bg-primary-500/15 flex items-center justify-center mb-3 group-hover:bg-primary-500/25">
        <span className="text-xl font-bold text-primary-400">{icon}</span>
      </div>
      <h3 className="font-medium text-gray-200">{title}</h3>
      <p className="text-sm text-gray-500">{description}</p>
    </button>
  );
}

interface BoardCardProps {
  name: string;
  workspace: string;
  lastUpdated: string;
  color: string;
  isFavorite?: boolean;
}

function BoardCard({ name, workspace, lastUpdated, color, isFavorite }: BoardCardProps) {
  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-lg overflow-hidden hover:border-white/20 transition-all cursor-pointer">
      {/* Color bar */}
      <div className="h-2" style={{ backgroundColor: color }} />

      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-medium text-gray-200">{name}</h3>
          {isFavorite && (
            <span className="text-yellow-500">★</span>
          )}
        </div>
        <p className="text-sm text-gray-500 mb-2">{workspace}</p>
        <p className="text-xs text-gray-600">Updated {lastUpdated}</p>
      </div>
    </div>
  );
}

/**
 * 404 Not Found Page
 */
function NotFoundPage() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0f' }}>
      <div className="text-center">
        <h1 className="text-8xl font-bold gold-shine mb-4">404</h1>
        <p className="text-xl text-gray-400 mb-6">Page not found</p>
        <a href="/" className="btn-primary">
          Go home
        </a>
      </div>
    </div>
  );
}

/**
 * Application Routes
 */
export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Auth routes - no AppShell */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* App routes - wrapped in AppShell and ProtectedRoute */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppShell>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/inbox" element={<PlaceholderPage title="Inbox" />} />
                  <Route path="/my-work" element={<PlaceholderPage title="My Work" />} />
                  <Route path="/calendar" element={<PlaceholderPage title="Calendar" />} />
                  <Route path="/dashboard" element={<PlaceholderPage title="Dashboard" />} />
                  <Route path="/settings" element={<PlaceholderPage title="Settings" />} />
                  <Route path="/boards/:workspaceId/:boardId" element={<BoardPage />} />
                  <Route path="/404" element={<NotFoundPage />} />
                  <Route path="*" element={<Navigate to="/404" replace />} />
                </Routes>
              </AppShell>
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthProvider>
  );
}

/**
 * Board Page
 */
function BoardPage() {
  return <BoardContainer board={DEMO_BOARD} />;
}

/**
 * Placeholder page for routes not yet implemented
 */
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-100 mb-2">{title}</h1>
        <p className="text-gray-500">This page will be implemented soon</p>
      </div>
    </div>
  );
}
